import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Check if the browser supports CSS ::details-content pseudo-element
 * This is a cutting-edge feature (Chrome 131+, Safari 18.2+) that many mobile browsers don't support yet
 */
const supportsDetailsContent = (() => {
  try {
    // Try to use CSS.supports to check for ::details-content support
    // Note: CSS.supports doesn't work well for pseudo-elements, so we use a different approach
    // Check for interpolate-size which is required for the CSS animation to work
    return CSS.supports('interpolate-size', 'allow-keywords');
  } catch {
    return false;
  }
})();

// Inject fallback styles for browsers that don't support modern CSS features
if (!supportsDetailsContent) {
  const style = document.createElement('style');
  style.textContent = `
    /* Fallback styles for browsers that don't support ::details-content */
    accordion-custom.js-fallback details .details-content {
      display: block !important;
      overflow: hidden !important;
      transition: none !important;
      /* Reset CSS properties that might interfere */
      block-size: auto !important;
    }
    accordion-custom.js-fallback details:not([open]) .details-content {
      height: 0 !important;
      opacity: 0 !important;
      padding-block: 0 !important;
    }
    accordion-custom.js-fallback details[open] .details-content {
      height: auto !important;
      opacity: 1 !important;
    }
    /* During animation, allow inline styles to control */
    accordion-custom.js-fallback.animating details .details-content {
      height: auto !important;
    }
  `;
  document.head.appendChild(style);
}

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get content() {
    return this.details.querySelector('.details-content');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #isAnimating = false;
  /** @type {Animation | null} */
  #animation = null;
  #useJSFallback = false;

  connectedCallback() {
    const { signal } = this.#controller;

    // Check if we need to use JS fallback
    this.#useJSFallback = !supportsDetailsContent && this.content !== null;

    if (this.#useJSFallback) {
      this.classList.add('js-fallback');
    }

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Cancel any running animation
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Animates the accordion content using JavaScript
   * @param {boolean} isOpening - Whether the accordion is opening or closing
   */
  #animateContent(isOpening) {
    const content = this.content;
    if (!content || this.#isAnimating) return;

    this.#isAnimating = true;
    this.classList.add('animating');

    // Cancel any running animation
    if (this.#animation) {
      this.#animation.cancel();
    }

    const duration = 300; // ms
    const easing = 'ease-out';

    if (isOpening) {
      // Opening animation
      // First, we need to measure the natural height
      content.style.height = 'auto';
      content.style.opacity = '1';
      content.style.paddingBlock = '';
      const endHeight = content.scrollHeight;
      content.style.height = '0px';
      content.style.opacity = '0';

      // Force a reflow to ensure the browser registers the starting state
      void content.offsetHeight;

      // Animate to the natural height
      const anim = content.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${endHeight}px`, opacity: 1 }
        ],
        { duration, easing, fill: 'forwards' }
      );
      this.#animation = anim;

      anim.onfinish = () => {
        content.style.height = '';
        content.style.opacity = '';
        this.#isAnimating = false;
        this.#animation = null;
        this.classList.remove('animating');
      };

      anim.oncancel = () => {
        this.#isAnimating = false;
        this.#animation = null;
        this.classList.remove('animating');
      };
    } else {
      // Closing animation
      const startHeight = content.scrollHeight;
      content.style.height = `${startHeight}px`;

      // Force a reflow
      void content.offsetHeight;

      const anim = content.animate(
        [
          { height: `${startHeight}px`, opacity: 1 },
          { height: '0px', opacity: 0 }
        ],
        { duration, easing, fill: 'forwards' }
      );
      this.#animation = anim;

      anim.onfinish = () => {
        content.style.height = '';
        content.style.opacity = '';
        this.details.open = false;
        this.#isAnimating = false;
        this.#animation = null;
        this.classList.remove('animating');
      };

      anim.oncancel = () => {
        this.#isAnimating = false;
        this.#animation = null;
        this.classList.remove('animating');
      };
    }
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // If browser doesn't support modern CSS features, handle animation via JS
    if (this.#useJSFallback) {
      event.preventDefault();

      const isCurrentlyOpen = this.details.open;

      if (isCurrentlyOpen) {
        // Closing - animate first, then close
        this.#animateContent(false);
      } else {
        // Opening - open first, then animate
        this.details.open = true;
        // Use requestAnimationFrame to ensure the DOM has updated
        requestAnimationFrame(() => {
          this.#animateContent(true);
        });
      }
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    const shouldBeOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    this.details.open = shouldBeOpen;
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      if (this.#useJSFallback && this.details.open) {
        this.#animateContent(false);
      } else {
        this.details.open = false;
      }
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
